import { AxiosProgressEvent } from 'axios';
import React, { useCallback, useState } from 'react';

import { Icon, Space, Typography, Uploader } from '@sravni/react-design-system';
import { Link, Trash } from '@sravni/react-icons';

import { IFile } from '../../../../interfaces/files';
import { uploadFile } from '../../../../services/files';

import styles from './upload-file.module.scss';

interface IProps {
  isReadOnly: boolean;
  handleUploadFile: (file: IFile) => void;
  removeExistedFile: (file: IFile) => void;
  removeNewFile: (file: IFile) => void;
  existedFiles: IFile[];
  onDownloadFiles: (attachmentIds: string[]) => Promise<void>;
}

const generateKeyFromFile = (file: File) => `${file.name}-${file.lastModified}`;

export const UploadFile: React.FC<IProps> = ({
  isReadOnly,
  existedFiles,
  handleUploadFile,
  removeExistedFile,
  removeNewFile,
  onDownloadFiles,
}) => {
  const [newFiles, setNewFiles] = useState<File[]>([]);
  const [newFilesMap, setNewFilesMap] = useState({});
  const [errorsFileKeys, setErrorsFileKeys] = useState([]);
  const [progressMap, setProgressMap] = useState({});

  const onUploadFile = useCallback(
    async (file: File) => {
      const key = generateKeyFromFile(file);
      const onProgress = (progress: AxiosProgressEvent) => {
        setProgressMap((prev) => ({
          ...prev,
          [key]: progress.loaded / progress.total,
        }));
      };
      setNewFiles((prev) => [...prev, file]);

      try {
        const result = await uploadFile(file, onProgress);
        handleUploadFile(result);
        setNewFilesMap((prev) => ({
          ...prev,
          [key]: result,
        }));
        setProgressMap((prev) => ({
          ...prev,
          [key]: 1,
        }));
      } catch (e) {
        setErrorsFileKeys((prev) => [...prev, key]);
        setProgressMap((prev) => ({
          ...prev,
          [key]: null,
        }));
      }
    },
    [handleUploadFile],
  );

  const handleAddingFiles = useCallback(
    (files: File[]) => {
      files.forEach((file) => onUploadFile(file));
    },
    [onUploadFile],
  );

  const handleRemoveNewFile = useCallback(
    (fileToDelete: File) => {
      const removedFileFromAttachments: IFile = newFilesMap[generateKeyFromFile(fileToDelete)];
      setNewFiles((prev) => prev.filter((file) => file !== fileToDelete));
      removeNewFile(removedFileFromAttachments);
    },
    [newFilesMap, removeNewFile],
  );

  return (
    <Space direction={'vertical'} size={16}>
      {!!existedFiles?.length && (
        <Space direction={'vertical'} size={12}>
          {existedFiles.map((attachment) => (
            <Space direction={'horizontal'} size={8} key={attachment.url}>
              <Typography.Text size={16} strong>
                {attachment.name}
              </Typography.Text>
              <Typography.Link onClick={() => onDownloadFiles([attachment.id])}>
                <Icon icon={<Link />} color={'blue'} />
              </Typography.Link>
              {!isReadOnly && (
                <Typography.Link
                  className={styles['deleteButton']}
                  onClick={() => removeExistedFile(attachment)}
                  title="Удалить файл"
                >
                  <Icon icon={<Trash />} />
                </Typography.Link>
              )}
            </Space>
          ))}
        </Space>
      )}

      {!isReadOnly && (
        <Uploader
          onFilesUpload={handleAddingFiles}
          multiple
          accept="image/jpeg, image/jpg, image/png, application/pdf"
          dragZoneDescription={
            'Загрузите сюда скрины из приложения, переписки или договора в формате jpeg, png, pdf до 10 МБ'
          }
        >
          {newFiles.map((file) => {
            const key = generateKeyFromFile(file);

            return (
              <Uploader.Item
                key={key}
                file={file}
                onRemove={handleRemoveNewFile}
                progressPercent={progressMap[key] && progressMap[key] * 100}
                errorMessage={errorsFileKeys.includes(key) && 'Ошибка при загрузке'}
              />
            );
          })}
        </Uploader>
      )}
    </Space>
  );
};
